// TraitementConges.jsx (RH/Admin) — Redesigné avec le style TraitementAvances

import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Search,
    Filter,
    Check,
    X,
    User,
    AlertCircle,
    FileText,
    ChevronDown,
    CalendarDays,
    Loader,
} from "lucide-react";
import {
    getAdminInboxRequest,
    adminDecisionRequest,
    clearLeaveRequestMessage,
} from "../../../../redux/actions/leaveRequestsActions";
import "../TraitementStyles/TraitementConges.css";

const TraitementConges = () => {
    const dispatch = useDispatch();

    // Redux state
    const leaveState = useSelector((state) => state.leaveRequests || {});
    const loading = !!leaveState.loading;
    const apiError = leaveState.error;
    const inbox = leaveState.adminInbox || [];
    const history = leaveState.adminHistory || [];

    const [activeTab, setActiveTab] = useState("enCours");
    const [searchTerm, setSearchTerm] = useState("");
    const [showFilters, setShowFilters] = useState(false);

    const [showDecisionModal, setShowDecisionModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedDemande, setSelectedDemande] = useState(null);
    const [decisionComment, setDecisionComment] = useState("");
    const [pendingDecision, setPendingDecision] = useState(null); // "approve" | "reject"

    const [filters, setFilters] = useState({
        typeConge: "",
        dateDebut: "",
        dateFin: "",
    });

    // Charger les données au montage
    useEffect(() => {
        dispatch(getAdminInboxRequest());
        dispatch({ type: "GET_ADMIN_HISTORY_REQUEST" }); // Suppose it exists or is added
    }, [dispatch]);

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        const d = new Date(dateStr);
        if (isNaN(d)) return dateStr; // retourne tel quel si pas parsable
        return d.toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    const mapStatusToTab = (s) => {
        const v = String(s || "").toUpperCase();
        if (v === "PENDING_ADMIN") return "enCours";
        if (v === "APPROVED") return "approuve";
        if (v === "REJECTED") return "rejete";
        if (v === "POSTPONED") return "reporte";
        if (v === "CANCELLED") return "annule";
        return "enCours";
    };

    const demandes = useMemo(() => {
        const combined = [...inbox, ...history];
        return combined.map((d) => ({
            id: d.id,
            matricule: d.employeeCode || "—",
            nom: d.employeeName || "—",
            typeConge: d.absenceTypeNom || "—",
            dateDebut: d.dateDebut,
            dateFin: d.dateFin,
            duree: d.duree ?? 0,
            motif: d.commentaire || "Non précisé",
            justificatif: d.justificatif || null,
            statut: mapStatusToTab(d.status),
            rawStatus: d.status,
            managerDecision: d.managerDecision ?? null,
            managerName: d.managerName || null,
            managerDecisionAt: d.managerDecisionAt || null,
            managerComment: d.managerComment || null,
            adminDecision: d.adminDecision ?? null,
            adminName: d.adminName || null,
            adminDecisionAt: d.adminDecisionAt || null,
            adminComment: d.adminComment || null,
        }));
    }, [inbox, history]);

    const getStatutBadge = (statut) => {
        const config = {
            enCours: { label: "En attente", bg: "#fff3cd", color: "#856404" },
            approuve: { label: "Approuvée", bg: "#d4edda", color: "#155724" },
            rejete: { label: "Rejetée", bg: "#f8d7da", color: "#721c24" },
            reporte: { label: "Reportée", bg: "#dbeafe", color: "#1d4ed8" },
            annule: { label: "Annulée", bg: "#f3f4f6", color: "#374151" },
        };
        const c = config[statut] || config.enCours;
        return (
            <span className="tcvBadge" style={{ backgroundColor: c.bg, color: c.color }}>
                {c.label}
            </span>
        );
    };

    const getFilteredDemandes = () => {
        let filtered = demandes.filter((d) => d.statut === activeTab);

        if (searchTerm) {
            const s = searchTerm.toLowerCase();
            filtered = filtered.filter(
                (d) =>
                    (d.nom && d.nom.toLowerCase().includes(s)) ||
                    (d.matricule && d.matricule.toLowerCase().includes(s)) ||
                    (d.typeConge && d.typeConge.toLowerCase().includes(s))
            );
        }

        if (filters.typeConge) {
            filtered = filtered.filter((d) => d.typeConge === filters.typeConge);
        }

        if (filters.dateDebut) {
            filtered = filtered.filter((d) => {
                if (!d.dateDebut) return true;
                const date = new Date(d.dateDebut).toISOString().split("T")[0];
                return date >= filters.dateDebut;
            });
        }

        if (filters.dateFin) {
            filtered = filtered.filter((d) => {
                if (!d.dateFin) return true;
                const date = new Date(d.dateFin).toISOString().split("T")[0];
                return date <= filters.dateFin;
            });
        }

        return filtered;
    };

    const tabs = useMemo(() => {
        return [
            {
                id: "enCours",
                label: "En cours",
                count: demandes.filter((d) => d.statut === "enCours").length,
            },
            {
                id: "approuve",
                label: "Approuvées",
                count: demandes.filter((d) => d.statut === "approuve").length,
            },
            {
                id: "rejete",
                label: "Rejetées",
                count: demandes.filter((d) => d.statut === "rejete").length,
            },
            {
                id: "reporte",
                label: "Reportées",
                count: demandes.filter((d) => d.statut === "reporte").length,
            },
            {
                id: "annule",
                label: "Annulées",
                count: demandes.filter((d) => d.statut === "annule").length,
            },
        ];
    }, [demandes]);

    const typesConges = useMemo(() => {
        return [...new Set(demandes.map((d) => d.typeConge).filter(Boolean))];
    }, [demandes]);

    // Ouvrir modal de décision (approuver ou rejeter)
    const handleOpenDecision = (demande, decision) => {
        setSelectedDemande(demande);
        setPendingDecision(decision);
        setDecisionComment("");
        setShowDecisionModal(true);
    };

    const confirmDecision = () => {
        if (!selectedDemande) return;
        const approved = pendingDecision === "approve";
        if (!approved && !decisionComment.trim()) return; // motif obligatoire pour rejet

        dispatch(
            adminDecisionRequest({
                id: selectedDemande.id,
                approved,
                comment: decisionComment || "",
            })
        );
        dispatch(clearLeaveRequestMessage());
        closeDecision();
    };

    const closeDecision = () => {
        setShowDecisionModal(false);
        setDecisionComment("");
        setSelectedDemande(null);
        setPendingDecision(null);
    };

    const showDetails = (demande) => {
        setSelectedDemande(demande);
        setShowDetailsModal(true);
    };

    const closeDetails = () => {
        setShowDetailsModal(false);
        setSelectedDemande(null);
    };

    const safeErrorText = (err) => {
        if (!err) return "";
        if (typeof err === "string") return err;
        if (err?.message) return String(err.message);
        try { return JSON.stringify(err); } catch { return String(err); }
    };

    const filtered = getFilteredDemandes();

    if (loading && demandes.length === 0) {
        return (
            <div className="tcvPage">
                <div className="tcvWrap" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
                    <Loader size={32} className="spin" />
                </div>
            </div>
        );
    }

    return (
        <div className="tcvPage">
            <div className="tcvWrap">
                {/* Header */}
                <div className="tcvHeader">
                    <div>
                        <h1>Traitement des demandes de congés</h1>
                        <p>Gérez et validez les demandes de congés et absences de vos collaborateurs</p>
                    </div>
                </div>

                {/* Search + Filters */}
                <div className="tcvCard">
                    <div className="tcvControls">
                        <div className="tcvSearch">
                            <Search size={18} />
                            <input
                                type="text"
                                placeholder="Rechercher par matricule, nom, type de congé..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <button type="button" className="tcvFilterBtn" onClick={() => setShowFilters((v) => !v)}>
                            <Filter size={16} />
                            Filtres
                            <ChevronDown size={16} className={`tcvChevron ${showFilters ? "is-open" : ""}`} />
                        </button>
                    </div>

                    {showFilters && (
                        <div className="tcvFilters">
                            <div className="tcvField">
                                <label>Type de congé</label>
                                <select value={filters.typeConge} onChange={(e) => setFilters((p) => ({ ...p, typeConge: e.target.value }))}>
                                    <option value="">Tous</option>
                                    {typesConges.map((t) => (
                                        <option key={t} value={t}>{t}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="tcvField">
                                <label>Date début (à partir du)</label>
                                <input
                                    type="date"
                                    value={filters.dateDebut}
                                    onChange={(e) => setFilters((p) => ({ ...p, dateDebut: e.target.value }))}
                                />
                            </div>

                            <div className="tcvField">
                                <label>Date fin (jusqu'au)</label>
                                <input
                                    type="date"
                                    value={filters.dateFin}
                                    onChange={(e) => setFilters((p) => ({ ...p, dateFin: e.target.value }))}
                                />
                            </div>
                        </div>
                    )}

                    {apiError && (
                        <div style={{ marginTop: 10, color: "#ef4444", fontSize: 12 }}>
                            {safeErrorText(apiError)}
                        </div>
                    )}
                </div>

                {/* Tabs */}
                <div className="tcvCard tcvCard--noPad">
                    <div className="tcvTabs">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => setActiveTab(tab.id)}
                                className={`tcvTab ${activeTab === tab.id ? "is-active" : ""}`}
                            >
                                {tab.label}
                                <span className={`tcvCount ${activeTab === tab.id ? "is-active" : ""}`}>
                                    {tab.count}
                                </span>
                            </button>
                        ))}
                    </div>

                    {/* Table */}
                    <div className="tcvTableWrap">
                        {filtered.length === 0 ? (
                            <div className="tcvEmpty">
                                <AlertCircle size={46} />
                                <div>Aucune demande trouvée</div>
                            </div>
                        ) : (
                            <table className="tcvTable">
                                <thead>
                                <tr>
                                    <th>Matricule</th>
                                    <th>Collaborateur</th>
                                    <th>Type de congé</th>
                                    <th>Période</th>
                                    <th>Durée</th>
                                    <th>Motif</th>
                                    <th>Statut</th>
                                    <th className="is-right">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {filtered.map((demande) => (
                                    <tr
                                        key={demande.id}
                                        onClick={() => showDetails(demande)}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <td>
                                            <span className="tcvPrimary">{demande.matricule}</span>
                                        </td>

                                        <td>
                                            <span className="tcvPrimary">{demande.nom}</span>
                                        </td>

                                        <td>
                                            <div className="tcvType">
                                                <span className="tcvDot" style={{ backgroundColor: "#3b82f6" }} />
                                                <span className="tcvPrimary">{demande.typeConge}</span>
                                            </div>
                                        </td>

                                        <td>
                                            <div className="tcvPrimary">{formatDate(demande.dateDebut)}</div>
                                            <div className="tcvSub">→ {formatDate(demande.dateFin)}</div>
                                        </td>

                                        <td>
                                            <span className="tcvPrimary">{demande.duree} jour{demande.duree > 1 ? "s" : ""}</span>
                                        </td>

                                        <td>
                                            <span className="tcvMuted" style={{ maxWidth: 160, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                {demande.motif}
                                            </span>
                                        </td>

                                        <td>{getStatutBadge(demande.statut)}</td>

                                        <td className="is-right" onClick={(e) => e.stopPropagation()}>
                                            {activeTab === "enCours" ? (
                                                <div className="tcvRowActions">
                                                    <button
                                                        type="button"
                                                        className="tcvActionBtn tcvActionBtn--ok"
                                                        onClick={() => handleOpenDecision(demande, "approve")}
                                                        title="Approuver"
                                                        disabled={loading}
                                                    >
                                                        <Check size={16} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="tcvActionBtn tcvActionBtn--no"
                                                        onClick={() => handleOpenDecision(demande, "reject")}
                                                        title="Refuser"
                                                        disabled={loading}
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="tcvDetailsBtn"
                                                    onClick={() => showDetails(demande)}
                                                >
                                                    Détails
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            {/* DETAILS MODAL */}
            {showDetailsModal && selectedDemande && (
                <div className="tcvModal">
                    <div className="tcvModal__panel" role="dialog" aria-modal="true">
                        <div className="tcvModal__header">
                            <div className="tcvModal__headLeft">
                                <div className="tcvModal__icon">
                                    <CalendarDays size={18} />
                                </div>
                                <div>
                                    <div className="tcvModal__title">Détails de la demande #{selectedDemande.id}</div>
                                    <div className="tcvModal__sub">Demande du {formatDate(selectedDemande.dateDebut)}</div>
                                </div>
                            </div>

                            <button type="button" className="tcvIconBtn" onClick={closeDetails} aria-label="Fermer">
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tcvModal__body">
                            <div className="tcvGrid2">
                                {/* Infos collaborateur */}
                                <div className="tcvPanel">
                                    <div className="tcvPanel__title">
                                        <User size={16} /> Informations collaborateur
                                    </div>
                                    <div className="tcvKv">
                                        <span>Matricule</span>
                                        <b>{selectedDemande.matricule}</b>
                                    </div>
                                    <div className="tcvKv">
                                        <span>Nom</span>
                                        <b>{selectedDemande.nom}</b>
                                    </div>
                                </div>

                                {/* Détails congé */}
                                <div className="tcvPanel">
                                    <div className="tcvPanel__title">
                                        <CalendarDays size={16} /> Détails du congé
                                    </div>
                                    <div className="tcvKv">
                                        <span>Type</span>
                                        <b>{selectedDemande.typeConge}</b>
                                    </div>
                                    <div className="tcvKv">
                                        <span>Date de début</span>
                                        <b className="tcvAccent">{formatDate(selectedDemande.dateDebut)}</b>
                                    </div>
                                    <div className="tcvKv">
                                        <span>Date de fin</span>
                                        <b className="tcvAccent">{formatDate(selectedDemande.dateFin)}</b>
                                    </div>
                                    <div className="tcvKv">
                                        <span>Durée</span>
                                        <b>{selectedDemande.duree} jour{selectedDemande.duree > 1 ? "s" : ""}</b>
                                    </div>
                                </div>

                                {/* Motif */}
                                <div className="tcvPanel tcvPanel--full">
                                    <div className="tcvPanel__title">
                                        <FileText size={16} /> Motif de la demande
                                    </div>
                                    <div className="tcvText">{selectedDemande.motif}</div>
                                </div>

                                {/* Justificatifs */}
                                {selectedDemande.justificatif && (
                                    <div className="tcvPanel">
                                        <div className="tcvPanel__title">
                                            <FileText size={16} /> Justificatif fourni
                                        </div>
                                        <div className="tcvTags">
                                            <span className="tcvTag">{selectedDemande.justificatif.split("/").pop()}</span>
                                        </div>
                                    </div>
                                )}

                                {/* Décision manager */}
                                {selectedDemande.managerDecision !== null && (
                                    <div className="tcvPanel tcvPanel--full">
                                        <div className="tcvPanel__title">
                                            <Check size={16} /> Validation Manager
                                        </div>
                                        <div className="tcvKv">
                                            <span>Décision</span>
                                            <b style={{ color: selectedDemande.managerDecision ? "#10b981" : "#ef4444" }}>
                                                {selectedDemande.managerDecision ? "Approuvé" : "Rejeté"}
                                            </b>
                                        </div>
                                        {selectedDemande.managerName && (
                                            <div className="tcvKv">
                                                <span>Par</span>
                                                <b>{selectedDemande.managerName}</b>
                                            </div>
                                        )}
                                        {selectedDemande.managerDecisionAt && (
                                            <div className="tcvKv">
                                                <span>Date</span>
                                                <b>{formatDate(selectedDemande.managerDecisionAt)}</b>
                                            </div>
                                        )}
                                        {selectedDemande.managerComment && (
                                            <div className="tcvRejectBox">
                                                <div className="tcvRejectLabel">Commentaire</div>
                                                <div className="tcvRejectText">{selectedDemande.managerComment}</div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Validation RH */}
                                {selectedDemande.statut !== "enCours" && selectedDemande.adminDecision !== null && (
                                    <div className={`tcvPanel tcvPanel--full ${selectedDemande.statut === "approuve" ? "tcvValidOk" : "tcvValidNo"}`}>
                                        <div className="tcvPanel__title">
                                            {selectedDemande.statut === "approuve" ? <Check size={16} /> : <X size={16} />}
                                            {selectedDemande.statut === "approuve" ? " Demande approuvée" : " Demande rejetée"}
                                        </div>
                                        <div className="tcvKv">
                                            <span>Date de validation</span>
                                            <b>{formatDate(selectedDemande.adminDecisionAt)}</b>
                                        </div>
                                        {selectedDemande.adminName && (
                                            <div className="tcvKv">
                                                <span>Validé par</span>
                                                <b>{selectedDemande.adminName}</b>
                                            </div>
                                        )}
                                        {selectedDemande.adminComment && (
                                            <div className="tcvRejectBox">
                                                <div className="tcvRejectLabel">
                                                    {selectedDemande.statut === "approuve" ? "Commentaire" : "Motif du rejet"}
                                                </div>
                                                <div className="tcvRejectText">{selectedDemande.adminComment}</div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Actions footer */}
                        <div className="tcvModal__footer">
                            {selectedDemande.statut === "enCours" ? (
                                <>
                                    <button
                                        type="button"
                                        className="tcvBtn tcvBtn--danger"
                                        onClick={() => {
                                            closeDetails();
                                            handleOpenDecision(selectedDemande, "reject");
                                        }}
                                        disabled={loading}
                                    >
                                        Refuser
                                    </button>
                                    <button
                                        type="button"
                                        className="tcvBtn tcvBtn--ok"
                                        onClick={() => {
                                            closeDetails();
                                            handleOpenDecision(selectedDemande, "approve");
                                        }}
                                        disabled={loading}
                                    >
                                        {loading ? <Loader size={16} className="spin" /> : "Approuver"}
                                    </button>
                                    <button type="button" className="tcvBtn tcvBtn--ghost" onClick={closeDetails}>
                                        Fermer
                                    </button>
                                </>
                            ) : (
                                <button type="button" className="tcvBtn tcvBtn--ghost" onClick={closeDetails}>
                                    Fermer
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* DECISION MODAL (Approuver ou Refuser) */}
            {showDecisionModal && selectedDemande && (
                <div className="tcvModal">
                    <div className="tcvModal__panel tcvModal__panel--small" role="dialog" aria-modal="true">
                        <div className="tcvModal__header">
                            <div className="tcvModal__headLeft">
                                <div className={`tcvModal__icon ${pendingDecision === "reject" ? "tcvModal__icon--danger" : ""}`}>
                                    {pendingDecision === "approve" ? <Check size={18} /> : <AlertCircle size={18} />}
                                </div>
                                <div>
                                    <div className="tcvModal__title">
                                        {pendingDecision === "approve" ? "Approuver la demande de congé" : "Refuser la demande de congé"}
                                    </div>
                                    <div className="tcvModal__sub">
                                        {selectedDemande.nom} — {selectedDemande.typeConge}
                                    </div>
                                </div>
                            </div>

                            <button type="button" className="tcvIconBtn" onClick={closeDecision} aria-label="Fermer">
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tcvModal__body">
                            <div className="tcvMiniCard">
                                <div className="tcvMiniRow">
                                    <span className="tcvMuted">Période</span>
                                    <b>{formatDate(selectedDemande.dateDebut)} → {formatDate(selectedDemande.dateFin)}</b>
                                </div>
                                <div className="tcvMiniRow">
                                    <span className="tcvMuted">Durée</span>
                                    <b className="tcvAccent">{selectedDemande.duree} jour{selectedDemande.duree > 1 ? "s" : ""}</b>
                                </div>
                            </div>

                            <label className="tcvLabel">
                                {pendingDecision === "reject" ? (
                                    <>Motif du refus <span className="tcvReq">*</span></>
                                ) : (
                                    <>Commentaire <span className="tcvMuted">(optionnel)</span></>
                                )}
                            </label>
                            <textarea
                                value={decisionComment}
                                onChange={(e) => setDecisionComment(e.target.value)}
                                rows={5}
                                placeholder={
                                    pendingDecision === "reject"
                                        ? "Veuillez préciser le motif du refus de cette demande..."
                                        : "Ajouter un commentaire (optionnel)..."
                                }
                                className="tcvTextarea"
                            />
                            <div className="tcvHelp">Ce commentaire sera communiqué au collaborateur</div>
                        </div>

                        <div className="tcvModal__footer">
                            <button type="button" className="tcvBtn tcvBtn--ghost" onClick={closeDecision}>
                                Annuler
                            </button>

                            {pendingDecision === "approve" ? (
                                <button
                                    type="button"
                                    className="tcvBtn tcvBtn--ok"
                                    onClick={confirmDecision}
                                    disabled={loading}
                                >
                                    {loading ? <Loader size={16} className="spin" /> : "Confirmer l'approbation"}
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="tcvBtn tcvBtn--danger"
                                    onClick={confirmDecision}
                                    disabled={!decisionComment.trim() || loading}
                                >
                                    {loading ? <Loader size={16} className="spin" /> : "Confirmer le refus"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TraitementConges;